/**
 * Shared environment variable validation helpers.
 * Each app (frontend, backend) calls parseEnv() at startup and will throw
 * immediately if required variables are missing or invalid.
 */
import { z } from 'zod';

// ── Database env ───────────────────────────────────────────────────────────────

const databaseSchema = z.object({
  // Either DATABASE_URL or individual components
  DATABASE_URL: z.string().optional().describe('PostgreSQL connection string (overrides component-based config)'),
  POSTGRES_USER: z.string().default('resumebuilder'),
  POSTGRES_PASSWORD: z.string().default('devpassword'),
  POSTGRES_HOST: z.string().default('localhost'),
  POSTGRES_PORT: z.coerce.number().int().positive().default(5432),
  POSTGRES_DB: z.string().default('resumebuilder'),
  POSTGRES_SSL_MODE: z.enum(['disable', 'require', 'verify-full']).optional(),
  POSTGRES_CA_CERT: z.string().optional().describe('PEM-encoded CA certificate (inline, takes precedence over POSTGRES_CA_CERT_PATH)'),
  POSTGRES_CA_CERT_PATH: z.string().optional(),
});

export const DatabaseEnvSchema = databaseSchema.transform((data) => ({
  ...data,
  DATABASE_URL: data.DATABASE_URL || buildDatabaseUrl(data),
}));

export type DatabaseEnv = z.infer<typeof DatabaseEnvSchema>;

function buildDatabaseUrl(data: {
  POSTGRES_USER: string;
  POSTGRES_PASSWORD: string;
  POSTGRES_HOST: string;
  POSTGRES_PORT: number;
  POSTGRES_DB: string;
  POSTGRES_SSL_MODE?: string | undefined;
}): string {
  const base = `postgres://${data.POSTGRES_USER}:${data.POSTGRES_PASSWORD}@${data.POSTGRES_HOST}:${data.POSTGRES_PORT}/${data.POSTGRES_DB}`;
  return data.POSTGRES_SSL_MODE
    ? `${base}?uselibpqcompat=true&sslmode=${data.POSTGRES_SSL_MODE}`
    : base;
}

// ── Backend env ────────────────────────────────────────────────────────────────

const backendSpecificSchema = z.object({
  NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
  PORT: z.coerce.number().int().positive().default(3001),
  HOST: z.string().default('0.0.0.0'),

  // CORS
  CORS_ORIGIN: z.string().default('http://localhost:5173'),

  // AI providers (at least one must be set for non-mock operation)
  ANTHROPIC_API_KEY: z.string().optional(),
  OPENAI_API_KEY: z.string().optional(),
  GOOGLE_API_KEY: z.string().optional(),

  // Email — Resend (optional; logs link to console when absent)
  RESEND_API_KEY: z.string().optional(),
  EMAIL_FROM: z.string().default('Resume Foundry <noreply@resumefoundry.app>'),

  // Signup token signing
  SIGNUP_TOKEN_SECRET: z.string().default('dev-signup-secret-change-in-production'),

  // Public URL of the frontend (used to build signup links in emails)
  APP_BASE_URL: z.string().default('http://localhost:5173'),

  // Residential proxy for URL fetching (optional)
  // Format: http://USERNAME:PASSWORD@HOSTNAME:PORT
  HTTP_PROXY_URL: z.string().url().optional(),
});

export const BackendEnvSchema = databaseSchema.merge(backendSpecificSchema).transform((data) => ({
  ...data,
  DATABASE_URL: data.DATABASE_URL || buildDatabaseUrl(data),
}));

export type BackendEnv = z.infer<typeof BackendEnvSchema>;

// ── Frontend env ───────────────────────────────────────────────────────────────

export const FrontendEnvSchema = z.object({
  VITE_API_URL: z.string().url().default('http://localhost:3001'),
});

export type FrontendEnv = z.infer<typeof FrontendEnvSchema>;

// ── Helper ─────────────────────────────────────────────────────────────────────

export function parseEnv<T extends z.ZodTypeAny>(
  schema: T,
  input: Record<string, string | undefined>,
): z.infer<T> {
  const result = schema.safeParse(input);
  if (!result.success) {
    const formatted = result.error.issues
      .map((issue) => `  ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');
    throw new Error(`Environment variable validation failed:\n${formatted}`);
  }
  return result.data as z.infer<T>;
}
